import {
  Action,
  AionzDialog,
  AionzSnackbar,
  AionzTooltip,
  ConfirmationModalService,
  DialogSize,
  ModalTypes
} from '@aionzdev/design-system';
import { Clipboard } from '@angular/cdk/clipboard';
import { CommonModule, formatCurrency, formatDate } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  viewChild
,
  LOCALE_ID
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatTable, MatTableModule } from '@angular/material/table';
import { DomSanitizer } from '@angular/platform-browser';
import { NgxMaskDirective } from 'ngx-mask';
import { catchError, debounceTime, distinctUntilChanged, of, Subject, take, takeUntil, tap } from 'rxjs';
import { AbstractUserService } from '@aionzdev/modules';
import { UserResponse } from '@aionzdev/modules';
import { AddSupplierToProductDto } from '../../../interfaces/add-supplier-to-product.dto';
import { ApproveQuotationMapPayload } from '../../../interfaces/approve-quotation-map';
import { PurchaseSupplier, PurchaseSupplierLinkStatus } from '../../../interfaces/purchase-supplier';
import { QuotationMapBoardData } from '../../../interfaces/quotation-map-board-data';
import { QuotationMapDraft, QuotationMapDraftSelection } from '../../../interfaces/quotation-map-draft';
import { QuotationMapFooterRowData } from '../../../interfaces/quotation-map-footer-row-data';
import { QuotationMapItem } from '../../../interfaces/quotation-map-item';
import {
  QuotationMapSupplier,
  QuotationMapSupplierStatus
} from '../../../interfaces/quotation-map-supplier';
import { QuotationMapSupplierFreight } from '../../../interfaces/quotation-map-supplier-freight';
import { AbstractPurchasingService } from '../../../services/abstract-purchasing.service';
import { CollapsibleMapColumn, QuotationMapPreferencesService, SupplierSort } from '../../../services/quotation-map-preferences.service';
import { KanbanChipBadgeComponent } from '../../atoms/kanban-chip-badge/kanban-chip-badge.component';
import { PurchaseHistoryTooltipComponent } from '../../atoms/purchase-history-tooltip/purchase-history-tooltip.component';
import { CollapsedSupplierTooltipComponent } from '../../atoms/collapsed-supplier-tooltip/collapsed-supplier-tooltip.component';
import { CollapsedSupplierSummary } from '../../../interfaces/collapsed-supplier-summary';
import { ApprovalReviewData, ApprovalReviewResult } from '../../../interfaces/approval-review';
import {
  buildApprovalReview,
  hasPricedSelection,
  supplierHeaderColors
} from '../../utils/approval-review.util';
import { ApprovalReviewDialogComponent } from '../approval-review-dialog/approval-review-dialog.component';
import {
  NewSupplierDialogComponent,
  NewSupplierDialogData,
  NewSupplierSaveEvent
} from '../new-supplier-dialog/new-supplier-dialog.component';
import { QuotationMapPendingActionButtonComponent } from '../quotation-map-pending-action-button/quotation-map-pending-action-button.component';

interface SupplierSelection {
  selected: boolean;
  negotiatedSelected: boolean;
  negotiatedPrice: number | null;
}

const DEFAULT_APPROVAL_JUSTIFICATION = 'Aprovação realizada sem justificativa.';

/**
 * Valor do campo de dinheiro como número. O ngx-mask costuma entregar número ou "1234.5", mas
 * se vier o texto exibido ("R$ 1.234,50"), converte do formato brasileiro.
 */
function parseMoney(value: unknown): number {
  if (typeof value === 'number') return value;
  const text = String(value ?? '').replace(/[^\d,.-]/g, '');
  if (!text) return 0;
  const normalized = text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text;
  return Number(normalized) || 0;
}

/** Evento de checkbox sintético, para reaproveitar os toggles a partir da digitação. */
function checkboxChange(checked: boolean): MatCheckboxChange {
  return { checked, source: { checked } } as unknown as MatCheckboxChange;
}

/** Colunas fixas que ordenam os itens pelo cabeçalho. */
type ItemSortKey = 'product' | 'branch';

const textCollator = new Intl.Collator('pt-BR', { numeric: true, sensitivity: 'base' });

function compareItems(a: QuotationMapItem, b: QuotationMapItem, key: ItemSortKey): number {
  return textCollator.compare(a[key] ?? '', b[key] ?? '');
}

/** Cores do pin por local de entrega (só quando o mapa tem mais de um). Contraste AA sobre branco. */
const BRANCH_COLORS = ['#1a6fb5', '#b25b00', '#7a4cc2', '#0f7f8c', '#b0306a', '#4d7a1f'];

@Component({
  selector: 'aionz-quotation-map-board',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatTableModule,
    MatButtonModule,
    MatCheckboxModule,
    MatCardModule,
    MatChipsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    NgxMaskDirective,
    QuotationMapPendingActionButtonComponent,
    NgxMaskDirective,
    AionzTooltip,
    KanbanChipBadgeComponent
  ],
  templateUrl: './quotation-map-board.component.html',
  styleUrls: ['./quotation-map-board.component.scss']
})
export class QuotationMapBoardComponent implements OnDestroy, AfterViewInit {
  data = input.required<QuotationMapBoardData>();
  /**
   * Só leitura (tela de aprovação): mostra o mapa com a seleção que veio nos dados, sem
   * rascunho, sem editar seleção, negociado ou frete e sem as ações de fornecedor.
   */
  readonly = input(false);
  onApprove = output<ApproveQuotationMapPayload>();
  onSendRequestEmail = output<{ supplierId: string }>();
  /** Pede à página para criar o fornecedor; `done(true)` fecha o dialog, `done(false)` o mantém aberto. */
  onCreateSupplier = output<NewSupplierSaveEvent>();
  onDeleteSupplier = output<{ supplierId: string }>();
  onAddSupplierToProduct = output<AddSupplierToProductDto>();
  /** Aceite ou recusa de sugestão pendente, por produto (`productIds` tem um item na ação de célula). */
  onUpdateSuggestedSupplierStatus = output<{
    supplierId: string;
    productIds: string[];
    status: PurchaseSupplierLinkStatus.APROVADO | PurchaseSupplierLinkStatus.REJEITADO;
  }>();
  /** Busca de fornecedores pela Cotação Inteligente: disparo fica na página, o painel só pede. */
  onSearchSuppliers = output<void>();
  supplierSearchLoading = input(false);
  supplierSearchProgress = input<{ done: number; total: number } | null>(null);
  lastSupplierSearchAt = input<string | Date | null | undefined>(null);
  quoteMapData = signal<QuotationMapBoardData>({} as QuotationMapBoardData);

  /**
   * Com mais de um local de entrega no mapa, o pin de cada item ganha a cor do seu local: dá para
   * separar os itens sem ler o nome em todas as linhas. Com um local só, o pin fica neutro.
   */
  readonly branchColors = computed(() => {
    const branches = Array.from(
      new Set((this.quoteMapData().items ?? []).map(item => item.branch).filter((b): b is string => !!b))
    );
    if (branches.length < 2) return new Map<string, string>();
    return new Map(branches.map((branch, i) => [branch, BRANCH_COLORS[i % BRANCH_COLORS.length]]));
  });

  /** Estado da persistência do rascunho, exibido no cabeçalho da página. */
  draftState = signal<'idle' | 'saving' | 'saved' | 'error'>('idle');
  draftSavedAt = signal<Date | null>(null);

  private readonly locale = inject(LOCALE_ID);

  /** Colunas de fornecedor recolhidas pelo usuário para facilitar a comparação. Estado só de UI. */
  hiddenSupplierIds = signal<ReadonlySet<string>>(new Set<string>());

  /** Ordenação das colunas de fornecedor. A cor de identidade acompanha o fornecedor, não a posição. */
  supplierSort = signal<SupplierSort>('default');

  private readonly preferences = inject(QuotationMapPreferencesService);

  /** Identidade de cor por fornecedor, aplicada só no cabeçalho da coluna: [fundo, barra]. */
  tableContainer = viewChild.required<ElementRef<HTMLDivElement>>('tableContainer');
  table = viewChild.required<MatTable<any>>(MatTable);

  private fb = inject(FormBuilder);
  private dialog = inject(AionzDialog);
  private _dialogService = inject(ConfirmationModalService);
  private userService = inject(AbstractUserService);
  private purchaseService = inject(AbstractPurchasingService);
  private snackBar = inject(AionzSnackbar);
  private clipboard = inject(Clipboard);
  private iconRegistry = inject(MatIconRegistry);
  private domSanitizer = inject(DomSanitizer);
  private destroy$ = new Subject<void>();
  private selectionsMap = new Map<string, SupplierSelection>();
  private freightSelectionsMap = new Map<string, SupplierSelection>();
  private initialized = false;
  /** `purchaseId` para o qual o rascunho do `/form-temp` já foi carregado. */
  private draftLoadedFor?: string;
  /** Débito de "salvar o rascunho" — coalesce mudanças de seleção/preço/frete. */
  private persistDraft$ = new Subject<void>();
  private resizeObserver?: ResizeObserver;
  protected readonly purchaseHistoryTooltipComponent = PurchaseHistoryTooltipComponent;
  protected readonly collapsedSupplierTooltipComponent = CollapsedSupplierTooltipComponent;
  protected readonly freightSupplierNotSelectedTooltip =
    'Não é possível selecionar um frete de um fornecedor não selecionado';
  protected readonly freightRequiredTooltip =
    'Todo fornecedor precisa de um valor de frete associado';

  negotiatedPricesForm: FormGroup = this.fb.group({});

  constructor() {
    this.iconRegistry.addSvgIcon(
      'earnings',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/earnings.svg')
    );
    this.iconRegistry.addSvgIcon(
      'outgoing_mail',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/outgoing_mail.svg')
    );

    effect(() => {
      const newData = this.data();
      if (!newData?.items?.length) return;

      if (!this.initialized) {
        this.initializeWithDraft(newData);
      } else {
        const selectedData = this.applySelections(newData);
        const syncedData = this.syncFreightSelectionForSuppliers(selectedData);
        this.quoteMapData.set(syncedData);
        this.syncFormControls(newData);
      }
      this.refreshStickyStyles();
    });

    this.persistDraft$
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe(() => this.persistDraft());
  }

  /**
   * Primeira montagem do board. Antes de renderizar, tenta carregar o rascunho
   * salvo (`/form-temp`) para este `purchaseId`; se houver, projeta as seleções
   * antes de `initializeFormControls()`. Se não houver (ou erro), abre limpo —
   * comportamento idêntico ao anterior.
   */
  private initializeWithDraft(newData: QuotationMapBoardData): void {
    const purchaseId = newData.purchaseId;
    this.restorePreferences(purchaseId, newData);

    const render = () => {
      const selectedData = this.applySelections(newData);
      const syncedData = this.syncFreightSelectionForSuppliers(selectedData);
      this.quoteMapData.set(syncedData);
      this.initializeFormControls();
      this.initialized = true;
      this.refreshStickyStyles();
    };

    if (this.readonly() || !purchaseId || this.draftLoadedFor === purchaseId) {
      render();
      return;
    }

    this.draftLoadedFor = purchaseId;
    this.purchaseService
      .getQuotationMapDraft(purchaseId)
      .pipe(
        take(1),
        catchError(() => of(null)),
        takeUntil(this.destroy$)
      )
      .subscribe(draft => {
        if (draft) this.applyDraft(draft);
        render();
      });
  }

  /** Converte o rascunho (Record) de volta para os `*SelectionsMap` (Map). */
  private applyDraft(draft: QuotationMapDraft): void {
    for (const [key, value] of Object.entries(draft.selections ?? {})) {
      this.selectionsMap.set(key, { ...value });
    }
    for (const [supplierId, value] of Object.entries(draft.freightSelections ?? {})) {
      this.freightSelectionsMap.set(supplierId, { ...value });
    }
  }

  /**
   * `*SelectionsMap` (Map) → rascunho serializável (Record). Só entra no draft
   * o que representa uma escolha real (selecionado, negociado, ou com preço
   * digitado) — entradas "tudo falso" são ruído deixado por marcar+desmarcar.
   */
  private buildDraft(): QuotationMapDraft {
    const isMeaningful = (s: SupplierSelection): boolean =>
      s.selected || s.negotiatedSelected || s.negotiatedPrice != null;

    const toRecord = (map: Map<string, SupplierSelection>): Record<string, QuotationMapDraftSelection> => {
      const record: Record<string, QuotationMapDraftSelection> = {};
      for (const [key, value] of map.entries()) {
        if (!isMeaningful(value)) continue;
        record[key] = {
          selected: value.selected,
          negotiatedSelected: value.negotiatedSelected,
          negotiatedPrice: value.negotiatedPrice
        };
      }
      return record;
    };

    return {
      selections: toRecord(this.selectionsMap),
      freightSelections: toRecord(this.freightSelectionsMap)
    };
  }

  /** Salva (ou apaga, se não sobrou nenhuma escolha) o rascunho em background. Best-effort. */
  private persistDraft(): void {
    // só leitura nunca grava rascunho (a seleção veio da aprovação, não do comprador)
    if (this.readonly()) return;
    const purchaseId = this.data()?.purchaseId;
    if (!purchaseId) return;

    const draft = this.buildDraft();
    const isEmpty =
      Object.keys(draft.selections).length === 0 &&
      Object.keys(draft.freightSelections).length === 0;

    const op$ = isEmpty
      ? this.purchaseService.deleteQuotationMapDraft(purchaseId)
      : this.purchaseService.saveQuotationMapDraft(purchaseId, draft);

    this.draftState.set('saving');
    op$
      .pipe(
        take(1),
        tap(() => {
          this.draftSavedAt.set(new Date());
          this.draftState.set('saved');
        }),
        catchError(() => {
          this.draftState.set('error');
          return of(void 0);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    const container = this.tableContainer().nativeElement;
    const syncViewportHeight = () => {
      container.style.setProperty('--qm-viewport-height', `${container.clientHeight}px`);
      this.table().updateStickyColumnStyles();
    };

    this.resizeObserver = new ResizeObserver(syncViewportHeight);
    this.resizeObserver.observe(container);
    syncViewportHeight();
  }

  private applySelections(data: QuotationMapBoardData): QuotationMapBoardData {
    const supplierFreights = new Map(
      Array.from((data.supplierFreights ?? new Map()).entries()).map(([id, freight]) => {
        const selection = this.freightSelectionsMap.get(id);
        return [
          id,
          selection
            ? {
                ...freight,
                selected: selection.selected,
                // Negociado marcado sem valor (rascunhos antigos) não vale: desmarca ao carregar.
                negotiatedSelected: selection.negotiatedSelected && (selection.negotiatedPrice ?? 0) > 0,
                negotiatedFreight: selection.negotiatedPrice
              }
            : freight
        ];
      })
    );

    if (this.selectionsMap.size === 0 && this.freightSelectionsMap.size === 0) {
      return { ...data, supplierFreights };
    }

    return {
      ...data,
      supplierFreights,
      items: data.items.map(item => ({
        ...item,
        suppliers: new Map(
          Array.from(item.suppliers.entries()).map(([id, supplier]) => {
            const key = this.getControlKey(item.code, id);
            const selection = this.selectionsMap.get(key);
            if (!selection) return [id, supplier];
            // Negociado marcado sem valor (rascunhos antigos) não vale: desmarca ao carregar.
            const negotiatedSelected = selection.negotiatedSelected && (selection.negotiatedPrice ?? 0) > 0;
            return [id, { ...supplier, ...selection, negotiatedSelected }];
          })
        )
      }))
    };
  }

  private syncFormControls(data: QuotationMapBoardData): void {
    data.items.forEach(item => {
      data.supplierIds.forEach(supplierId => {
        const controlKey = this.getControlKey(item.code, supplierId);
        if (!this.negotiatedPricesForm.get(controlKey)) {
          const supplier = item.suppliers.get(supplierId);
          const control = new FormControl(supplier?.negotiatedPrice ?? null);

          control.valueChanges
            .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(value => this.updateNegotiatedPrice(item.code, supplierId, value));

          this.negotiatedPricesForm.addControl(controlKey, control);
        }
      });
    });

    data.supplierIds.forEach(supplierId => {
      this.ensureFreightFormControl(supplierId, data.supplierFreights?.get(supplierId));
    });
  }

  private initializeFormControls(): void {
    const controls: Record<string, FormControl> = {};

    this.quoteMapData().items.forEach(item => {
      this.quoteMapData().supplierIds.forEach(supplierId => {
        const controlKey = this.getControlKey(item.code, supplierId);
        const supplier = item.suppliers.get(supplierId);
        const initialValue = supplier?.negotiatedPrice ?? null;

        controls[controlKey] = new FormControl(initialValue);

        controls[controlKey].valueChanges
          .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
          .subscribe(value => {
            this.updateNegotiatedPrice(item.code, supplierId, value);
          });
      });
    });

    this.quoteMapData().supplierIds.forEach(supplierId => {
      const freightKey = this.getFreightControlKey(supplierId);
      const freight = this.quoteMapData().supplierFreights?.get(supplierId);
      controls[freightKey] = new FormControl(freight?.negotiatedFreight ?? null);
      controls[freightKey].valueChanges
        .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
        .subscribe(value => this.updateNegotiatedFreight(supplierId, value));
    });

    this.negotiatedPricesForm = this.fb.group(controls);
  }

  private ensureFreightFormControl(
    supplierId: string,
    freight?: QuotationMapSupplierFreight
  ): void {
    const freightKey = this.getFreightControlKey(supplierId);
    if (this.negotiatedPricesForm.get(freightKey)) return;

    const control = new FormControl(freight?.negotiatedFreight ?? null);
    control.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => this.updateNegotiatedFreight(supplierId, value));
    this.negotiatedPricesForm.addControl(freightKey, control);
  }

  private getControlKey(itemCode: string, supplierId: string): string {
    return `${itemCode}_${supplierId}`;
  }

  private getFreightControlKey(supplierId: string): string {
    return `freight_${supplierId}`;
  }

  private updateSelectionsMap(
    itemCode: string,
    supplierId: string,
    updates: Partial<SupplierSelection>
  ): void {
    const key = this.getControlKey(itemCode, supplierId);
    const existing = this.selectionsMap.get(key) || {
      selected: false,
      negotiatedSelected: false,
      negotiatedPrice: null
    };
    const next = { ...existing, ...updates };
    this.selectionsMap.set(key, next);
    // Só persiste quando algo mudou de fato: refresh por websocket reaplica as mesmas
    // seleções e não deve gravar rascunho nem mostrar "Rascunho salvo" sem ação do usuário.
    if (!this.isSameSelection(existing, next)) this.scheduleDraftPersist();
  }

  private isSameSelection(a: SupplierSelection, b: SupplierSelection): boolean {
    return (
      a.selected === b.selected &&
      !!a.negotiatedSelected === !!b.negotiatedSelected &&
      (a.negotiatedPrice ?? null) === (b.negotiatedPrice ?? null)
    );
  }

  private updateFreightSelectionsMap(
    supplierId: string,
    updates: Partial<SupplierSelection>
  ): void {
    const existing = this.freightSelectionsMap.get(supplierId) || {
      selected: false,
      negotiatedSelected: false,
      negotiatedPrice: null
    };
    const next = { ...existing, ...updates };
    this.freightSelectionsMap.set(supplierId, next);
    if (!this.isSameSelection(existing, next)) this.scheduleDraftPersist();
  }

  /**
   * Agenda um save do rascunho — mas só depois da inicialização. Durante a
   * primeira montagem, `syncFreightSelectionForSuppliers()` já escreve no
   * `freightSelectionsMap` (auto-seleção de frete): isso não é uma escolha do
   * usuário e não deve gerar um rascunho "só por abrir".
   */
  private scheduleDraftPersist(): void {
    if (!this.initialized) return;
    this.persistDraft$.next();
  }

  getFormControl(itemCode: string, supplierId: string): FormControl {
    const controlKey = this.getControlKey(itemCode, supplierId);
    return this.negotiatedPricesForm.get(controlKey) as FormControl;
  }

  getFreightFormControl(supplierId: string): FormControl {
    return this.negotiatedPricesForm.get(this.getFreightControlKey(supplierId)) as FormControl;
  }

  /**
   * Ids de fornecedor na ordem de exibição. Fornecedores que ainda não responderam vão
   * sempre para o fim; empates mantêm a ordem original.
   */
  orderedSupplierIds = computed<string[]>(() => {
    const data = this.quoteMapData();
    const ids = data.supplierIds ?? [];
    const sort = this.supplierSort();
    if (sort === 'default') return ids;

    const responded = (id: string) => !!this.getSupplierRespondedAt(id);
    return ids
      .map((id, index) => ({ id, index }))
      .sort((a, b) => {
        const ra = responded(a.id);
        const rb = responded(b.id);
        if (ra !== rb) return ra ? -1 : 1;
        if (sort === 'total') {
          const ta = this.getSupplierQuotedTotal(a.id) ?? Number.POSITIVE_INFINITY;
          const tb = this.getSupplierQuotedTotal(b.id) ?? Number.POSITIVE_INFINITY;
          if (ta !== tb) return ta - tb;
        } else {
          const ca = this.getSupplierBestPriceCount(a.id);
          const cb = this.getSupplierBestPriceCount(b.id);
          if (ca !== cb) return cb - ca;
        }
        return a.index - b.index;
      })
      .map(entry => entry.id);
  });

  /**
   * Largura da coluna de código ajustada ao maior código presente (em `ch`, que resolve na fonte
   * da própria célula), com o cabeçalho "Código" como piso e o padding horizontal da célula somado.
   */
  codeColumnWidth = computed<string>(() => {
    const longest = (this.quoteMapData().items ?? []).reduce(
      (max, item) => Math.max(max, String(item.code ?? '').length),
      'Código'.length
    );
    // piso de 72px: a palavra "Código" tem letras mais largas que dígitos e não cabe em 6ch
    return `max(72px, calc(${longest}ch + 26px))`;
  });

  /** Itens com algum preço (cotado ou negociado) selecionado. */
  selectedItemsCount = computed<number>(() =>
    (this.quoteMapData().items ?? []).filter(item =>
      Array.from(item.suppliers.values()).some(s => s.selected || s.negotiatedSelected)
    ).length
  );

  itemsCount = computed<number>(() => (this.quoteMapData().items ?? []).length);

  /** Fornecedores com item selecionado e cotação vencida; alimenta o aviso da página e o dialog. */
  expiredSelectedSuppliers = computed<string[]>(() => this.getExpiredSelectedSupplierNames());

  hasHiddenSuppliers = computed<boolean>(() => this.hiddenSupplierIds().size > 0);

  /** Há o que recolher: existe seleção e ao menos um fornecedor visível sem nenhum item selecionado. */
  canCollapseUnselected = computed<boolean>(() => {
    if (this.selectedItemsCount() === 0) return false;
    const hidden = this.hiddenSupplierIds();
    return (this.quoteMapData().supplierIds ?? []).some(id => !hidden.has(id) && !this.hasSelectedProducts(id));
  });

  /** Locais de entrega distintos dos itens do mapa (chip no topo da página). */
  readonly deliveryBranches = computed(() =>
    Array.from(new Set((this.quoteMapData().items ?? []).map(item => item.branch).filter((b): b is string => !!b)))
  );

  /** Atalho do chip de locais no topo: agrupa os itens por local de entrega. */
  sortItemsByBranch(): void {
    this.itemSort.set({ key: 'branch', direction: 'asc' });
  }

  /** Coluna Local aparece sempre que algum item tem local, na posição fixa depois do Cód. ERP. */
  protected readonly hasBranches = computed(() => this.deliveryBranches().length > 0);

  /** Ordenação dos itens pelo cabeçalho das colunas fixas; null = ordem original da solicitação. */
  protected readonly itemSort = signal<{ key: ItemSortKey; direction: 'asc' | 'desc' } | null>(null);

  /** "0104 · Matriz" → "0104": a coluna mostra só o código; o nome inteiro fica no tooltip. */
  protected branchShortLabel(branch: string): string {
    return branch.split('·')[0].trim() || branch;
  }

  /** Cada clique no cabeçalho: crescente → decrescente → ordem original. */
  protected toggleItemSort(key: ItemSortKey): void {
    this.itemSort.update(current => {
      if (current?.key !== key) return { key, direction: 'asc' };
      return current.direction === 'asc' ? { key, direction: 'desc' } : null;
    });
  }

  protected sortDirection(key: ItemSortKey): 'asc' | 'desc' | null {
    const sort = this.itemSort();
    return sort?.key === key ? sort.direction : null;
  }

  protected sortAria(key: ItemSortKey): 'ascending' | 'descending' | 'none' {
    const direction = this.sortDirection(key);
    return direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none';
  }

  /** Coluna do código ERP só quando algum item tem código. */
  protected readonly hasErpCodes = computed(() => (this.quoteMapData().items ?? []).some(item => !!item.ERPId));

  /** Colunas fixas compactadas (só ícone); vale para todos os mapas, guardado no navegador. */
  protected readonly collapsedColumns = signal<ReadonlySet<CollapsibleMapColumn>>(
    // Sem preferência salva, Local começa compactada (só o pin); o nome fica no tooltip.
    new Set(this.preferences.getGlobal().collapsedColumns ?? ['branch'])
  );

  protected isColumnCollapsed(column: CollapsibleMapColumn): boolean {
    return this.collapsedColumns().has(column);
  }

  protected toggleColumnCollapsed(column: CollapsibleMapColumn): void {
    this.collapsedColumns.update(current => {
      const next = new Set(current);
      if (next.has(column)) next.delete(column);
      else next.add(column);
      return next;
    });
    this.preferences.patchGlobal({ collapsedColumns: [...this.collapsedColumns()] });
    this.refreshStickyStyles();
  }

  displayedColumns = computed(() => {
    const baseColumns = ['code', 'product'];
    if (this.hasErpCodes()) baseColumns.push('erp');
    if (this.hasBranches()) baseColumns.push('branch');
    baseColumns.push('quantity', 'unit', 'history');
    const columns = [...baseColumns, ...this.orderedSupplierIds()];
    return this.readonly() ? columns : [...columns, 'supplierActions'];
  });

  tableDataSource = computed(() => {
    const items = this.quoteMapData().items ?? [];
    const sort = this.itemSort();
    // Ordenação estável: itens empatados mantêm a ordem original entre si.
    const productRows = sort
      ? items
          .map((item, index) => ({ item, index }))
          .sort((a, b) => {
            const result = compareItems(a.item, b.item, sort.key);
            return (sort.direction === 'asc' ? result : -result) || a.index - b.index;
          })
          .map(({ item }) => item)
      : [...items];
    const validityRow = { isValidityRow: true, isFooterRow: false };
    const footerRows: QuotationMapFooterRowData[] = [
      { isFooterRow: true, type: 'observations', label: 'Observações' },
      { isFooterRow: true, type: 'paymentMethod', label: 'Formas de pagamento' },
      { isFooterRow: true, type: 'paymentTerms', label: 'Prazo do pagamento' },
      { isFooterRow: true, type: 'freight', label: 'Frete' }
    ];
    return [validityRow, ...productRows, ...footerRows];
  });

  productTotal = computed(() => {
    return (itemCode: string, supplierId: string): number => {
      const item = this.quoteMapData().items.find(item => item.code === itemCode);
      if (!item) return 0;

      const supplier = item.suppliers.get(supplierId);
      if (!supplier) return 0;

      return supplier.selected
        ? supplier.price * item.quantity
        : supplier.negotiatedSelected && supplier.negotiatedPrice
          ? supplier.negotiatedPrice * item.quantity
          : 0;
    };
  });

  totalValue = computed(() => {
    const productsTotal = this.quoteMapData().items.reduce((total, item) => {
      const sum = Array.from(item.suppliers.values()).reduce((subtotal, s) => {
        const original = s.selected ? s.price * item.quantity : 0;
        const negotiated =
          s.negotiatedSelected && s.negotiatedPrice != null ? s.negotiatedPrice * item.quantity : 0;

        return subtotal + original + negotiated;
      }, 0);
      return total + sum;
    }, 0);

    const freightTotal = this.quoteMapData().supplierIds.reduce((total, supplierId) => {
      return total + this.getSelectedFreightValue(supplierId);
    }, 0);

    return productsTotal + freightTotal;
  });

  allSelected = computed(() => {
    const items = this.quoteMapData().items;
    if (items.length === 0) return false;
    return items.every(item =>
      Array.from(item.suppliers.values()).some(s => s.selected && s.bestPrice)
    );
  });

  someSelected = computed(() => {
    const items = this.quoteMapData().items;
    const hasSelected = items.some(item =>
      Array.from(item.suppliers.values()).some(s => s.selected && s.bestPrice)
    );
    return hasSelected && !this.allSelected();
  });

  totalSaving = computed(() => {
    const productsSaving = this.quoteMapData().items.reduce((saving, item) => {
      return (
        saving +
        Array.from(item.suppliers.values()).reduce((itemSaving, s) => {
          if (s.negotiatedSelected && s.negotiatedPrice != null && s.price) {
            const difference = s.price - s.negotiatedPrice;
            const totalDifference = difference > 0 ? difference * item.quantity : 0;
            return itemSaving + totalDifference;
          }
          return itemSaving;
        }, 0)
      );
    }, 0);

    const freightSaving = this.quoteMapData().supplierIds.reduce((saving, supplierId) => {
      if (!this.hasSelectedProducts(supplierId)) return saving;

      const freight = this.getSupplierFreight(supplierId);
      if (!freight?.negotiatedSelected || freight.negotiatedFreight == null) return saving;
      const difference = freight.freight - freight.negotiatedFreight;
      return saving + (difference > 0 ? difference : 0);
    }, 0);

    return productsSaving + freightSaving;
  });

  isNegotiatedFieldDisabled(item: QuotationMapItem, supplierId: string) {
    return computed(() => {
      const supplier = this.getQuotationMapSupplier(item, supplierId);
      if (!supplier) return true;
      // Sempre editável (fora do só leitura): digitar já passa a valer o negociado (onNegotiatedTyped).
      const disabled = this.readonly();
      const controlKey = this.getControlKey(item.code, supplierId);
      const control = this.negotiatedPricesForm.get(controlKey);

      if (control) {
        if (disabled) {
          control.disable();
        } else {
          control.enable();
        }
      }

      return disabled;
    });
  }

  isNegotiatedFreightDisabled(supplierId: string) {
    return computed(() => {
      const hasProducts = this.hasSelectedProducts(supplierId);
      const freight = this.getSupplierFreight(supplierId);
      // Editável sempre que o fornecedor tem item selecionado; digitar já passa a valer o negociado.
      const disabled = this.readonly() || !hasProducts || !freight;
      const control = this.getFreightFormControl(supplierId);

      if (control) {
        if (disabled) {
          control.disable();
        } else {
          control.enable();
        }
      }

      return disabled;
    });
  }

  toggleAll(event: MatCheckboxChange): void {
    const checked = event.checked;
    const updatedData = { ...this.quoteMapData() };

    updatedData.items = updatedData.items.map(item => ({
      ...item,
      suppliers: new Map(
        Array.from(item.suppliers.entries()).map(([id, supplier]) => {
          const newSelection: Partial<SupplierSelection> = supplier.bestPrice
            ? {
                selected: checked,
                negotiatedSelected: !checked ? supplier.negotiatedSelected : false
              }
            : { selected: false, negotiatedSelected: false };

          this.updateSelectionsMap(item.code, id, {
            selected: newSelection.selected!,
            negotiatedSelected: newSelection.negotiatedSelected!
          });

          return [id, { ...supplier, ...newSelection }];
        })
      )
    }));

    this.quoteMapData.set(this.syncFreightSelectionForSuppliers(updatedData));
  }

  /**
   * Digitou um negociado: ele passa a valer (antes o comprador precisava marcar a caixinha, e um
   * valor digitado com o cotado marcado ficava sem efeito). Apagou: volta ao cotado do mesmo
   * fornecedor. Só na digitação do usuário: restaurar rascunho não mexe na seleção.
   */
  /**
   * Largura do campo de negociado pelo texto: compacto vazio ("R$ 0,00" cabe em 72px) e crescendo
   * aos poucos com o valor digitado, até 140px. Recalcula a cada tecla (o evento dispara a detecção).
   */
  negotiatedFieldWidth(displayed: string | null | undefined): number {
    const chars = Math.max((displayed ?? '').length, 7);
    return Math.min(140, Math.max(72, Math.round(chars * 7.4 + 22)));
  }

  onNegotiatedTyped(code: string, supplierId: string): void {
    // O ngx-mask grava o valor no controle no mesmo evento de input: lê depois dele.
    setTimeout(() => this.applyTypedNegotiated(code, supplierId));
  }

  private applyTypedNegotiated(code: string, supplierId: string): void {
    const value = parseMoney(this.getFormControl(code, supplierId)?.value);
    const quote = this.quoteMapData().items.find(item => item.code === code)?.suppliers.get(supplierId);
    if (!quote) return;
    if (value > 0 && !quote.negotiatedSelected) {
      this.toggleNegotiated(code, supplierId, checkboxChange(true));
    } else if (value === 0 && quote.negotiatedSelected) {
      this.toggleSelection(code, supplierId, checkboxChange(true));
    }
  }

  /** Mesmo comportamento do negociado do item, para o frete. */
  onNegotiatedFreightTyped(supplierId: string): void {
    setTimeout(() => this.applyTypedNegotiatedFreight(supplierId));
  }

  private applyTypedNegotiatedFreight(supplierId: string): void {
    if (!this.hasSelectedProducts(supplierId)) return;
    const value = parseMoney(this.getFreightFormControl(supplierId)?.value);
    const freight = this.getSupplierFreight(supplierId);
    if (!freight) return;
    if (value > 0 && !freight.negotiatedSelected) {
      this.toggleNegotiatedFreight(supplierId, checkboxChange(true));
    } else if (value === 0 && freight.negotiatedSelected) {
      this.toggleFreightSelection(supplierId, checkboxChange(true));
    }
  }

  toggleSelection(code: string, supplierId: string, event: MatCheckboxChange): void {
    const checked = event.checked;
    const updatedData = { ...this.quoteMapData() };
    const itemIndex = updatedData.items.findIndex(item => item.code === code);

    if (itemIndex !== -1) {
      const item = updatedData.items[itemIndex];
      const updatedSuppliers = new Map(item.suppliers);

      if (checked) {
        updatedSuppliers.forEach((supplier, id) => {
          updatedSuppliers.set(id, {
            ...supplier,
            selected: id === supplierId,
            negotiatedSelected: false
          });
          this.updateSelectionsMap(code, id, {
            selected: id === supplierId,
            negotiatedSelected: false
          });
        });
      } else {
        const supplier = updatedSuppliers.get(supplierId);
        if (supplier) {
          updatedSuppliers.set(supplierId, {
            ...supplier,
            selected: false,
            negotiatedSelected: false
          });

          this.updateSelectionsMap(code, supplierId, {
            selected: false,
            negotiatedSelected: false
          });
        }
      }

      updatedData.items[itemIndex] = { ...item, suppliers: updatedSuppliers };
    }

    this.quoteMapData.set(this.syncFreightSelectionForSuppliers(updatedData));
  }

  toggleNegotiated(
    code: string,
    supplierId: string,
    event: MatCheckboxChange,
    input?: HTMLInputElement
  ): void {
    const checked = event.checked;
    // Marcar o negociado sem valor deixaria o item "selecionado" sem preço: em vez disso,
    // leva ao campo. Digitar o valor já marca o negociado (onNegotiatedTyped).
    if (checked && !(parseMoney(this.getFormControl(code, supplierId)?.value) > 0)) {
      event.source.checked = false;
      input?.focus();
      this.snackBar.info('Digite o valor negociado: ao digitar, ele passa a valer para o item.');
      return;
    }
    const updatedData = { ...this.quoteMapData() };
    const itemIndex = updatedData.items.findIndex(item => item.code === code);

    if (itemIndex !== -1) {
      const item = updatedData.items[itemIndex];
      const supplier = item.suppliers.get(supplierId) || this.createSupplierEntry(supplierId);
      const updatedSuppliers = new Map(item.suppliers);

      if (checked) {
        updatedSuppliers.forEach((supplierData, id) => {
          updatedSuppliers.set(id, {
            ...supplierData,
            selected: false,
            negotiatedSelected: id === supplierId
          });

          this.updateSelectionsMap(code, id, {
            selected: false,
            negotiatedSelected: id === supplierId
          });
        });

        this.updateNegotiatedPrice(
          code,
          supplierId,
          this.getFormControl(code, supplierId).value || null
        );
      } else {
        updatedSuppliers.set(supplierId, {
          ...supplier,
          negotiatedSelected: false,
          selected: false
        });

        this.updateSelectionsMap(code, supplierId, {
          selected: false,
          negotiatedSelected: false
        });
      }

      updatedData.items[itemIndex] = { ...item, suppliers: updatedSuppliers };
      this.quoteMapData.set(this.syncFreightSelectionForSuppliers(updatedData));

      if (checked && input) {
        setTimeout(() => input.focus());
      }
    }
  }

  getSupplierHeaderColors(supplierIndex: number): { background: string; bar: string } {
    return supplierHeaderColors(supplierIndex);
  }

  /**
   * Negociar para cima quase sempre é erro de digitação: destaca sem bloquear.
   * Só avisa quando o negociado está marcado, porque desmarcado ele não entra no total.
   */
  isNegotiatedAboveQuoted(quote: QuotationMapSupplier): boolean {
    return (
      !!quote.negotiatedSelected &&
      quote.negotiatedPrice != null &&
      quote.price != null &&
      quote.negotiatedPrice > quote.price
    );
  }

  isNegotiatedFreightAboveQuoted(supplierId: string): boolean {
    const freight = this.getSupplierFreight(supplierId);
    return (
      !!freight &&
      !!freight.negotiatedSelected &&
      freight.negotiatedFreight != null &&
      freight.negotiatedFreight > freight.freight
    );
  }

  getNegotiatedPriceTooltip(quote: QuotationMapSupplier): string {
    if (!this.isNegotiatedAboveQuoted(quote)) {
      return 'Insira aqui o valor final negociado com o fornecedor.';
    }
    return `Valor negociado (${this.formatBRL(quote.negotiatedPrice!)}) acima do preço cotado (${this.formatBRL(quote.price)}). Confira o valor digitado.`;
  }

  getNegotiatedFreightTooltip(supplierId: string): string {
    const freight = this.getSupplierFreight(supplierId);
    if (!freight || !this.isNegotiatedFreightAboveQuoted(supplierId)) {
      return 'Insira aqui o valor de frete negociado com o fornecedor.';
    }
    return `Frete negociado (${this.formatBRL(freight.negotiatedFreight!)}) acima do frete cotado (${this.formatBRL(freight.freight)}). Confira o valor digitado.`;
  }

  private formatBRL(value: number): string {
    return formatCurrency(value, this.locale, 'R$', 'BRL', '1.2-2');
  }

  getSupplierSearchTooltip(): string {
    const progress = this.supplierSearchProgress();
    if (this.supplierSearchLoading() && progress) {
      return `Buscando fornecedores: ${progress.done} de ${progress.total} produtos analisados`;
    }
    const base = 'Buscar novos fornecedores com a Cotação Inteligente';
    const last = this.lastSupplierSearchAt();
    if (!last) return base;
    const date = last instanceof Date ? last : new Date(last);
    if (Number.isNaN(date.getTime())) return base;
    return `${base}. Última busca: ${formatDate(date, 'dd/MM/yy HH:mm', this.locale)}`;
  }

  /** Resumo da proposta para o tooltip da coluna recolhida. */
  getCollapsedSupplierSummary(supplierId: string): CollapsedSupplierSummary {
    const data = this.quoteMapData();
    const index = (data.supplierIds ?? []).indexOf(supplierId);
    const items = (data.items ?? []).map(item => {
      const quote = item.suppliers.get(supplierId);
      const price = quote?.price ? quote.price : null;
      return {
        code: item.code,
        product: item.product,
        quantity: item.quantity,
        unit: item.unit,
        price,
        bestPrice: !!quote?.bestPrice,
        selected: !!(quote?.selected || quote?.negotiatedSelected)
      };
    });
    const quoted = items.filter(i => i.price != null);
    const responded = !!this.getSupplierRespondedAt(supplierId);
    return {
      name: this.getSupplierName(supplierId),
      email: this.getSupplierEmail(supplierId),
      accentColor: this.getSupplierHeaderColors(Math.max(index, 0)).bar,
      responded,
      respondedAt: this.getSupplierRespondedAt(supplierId) || undefined,
      validityLabel: responded ? this.getValidityLabel(supplierId) : undefined,
      validityStatus: responded ? this.getSupplierValidityStatus(supplierId) : null,
      items,
      paymentMethod: responded ? String(this.getSupplierFooterData(supplierId, 'paymentMethod')) : undefined,
      paymentTerms: responded ? String(this.getSupplierFooterData(supplierId, 'paymentTerms')) : undefined,
      freight: this.getSupplierFreight(supplierId)?.freight ?? null,
      quotedTotal: quoted.length ? quoted.reduce((sum, i) => sum + (i.price as number) * i.quantity, 0) : null,
      quotedCount: quoted.length,
      bestPriceCount: items.filter(i => i.bestPrice).length,
      selectedCount: items.filter(i => i.selected).length
    };
  }

  isSupplierHidden(supplierId: string): boolean {
    return this.hiddenSupplierIds().has(supplierId);
  }

  getSelectedProductsCount(supplierId: string): number {
    return (this.quoteMapData().items ?? []).filter(item => {
      const supplier = item.suppliers.get(supplierId);
      return !!supplier && (supplier.selected || supplier.negotiatedSelected);
    }).length;
  }

  getHideSupplierTooltip(supplierId: string): string {
    const count = this.getSelectedProductsCount(supplierId);
    if (count === 0) return 'Ocultar coluna';
    const items = count === 1 ? '1 item selecionado' : `${count} itens selecionados`;
    return `Coluna com ${items}. Selecione esses itens em outra coluna para poder ocultar.`;
  }

  toggleSupplierHidden(supplierId: string): void {
    const hidden = this.isSupplierHidden(supplierId);
    if (!hidden && this.getSelectedProductsCount(supplierId) > 0) return;
    this.hiddenSupplierIds.update(current => {
      const next = new Set(current);
      if (hidden) next.delete(supplierId);
      else next.add(supplierId);
      return next;
    });
    this.preferences.patch(this.data()?.purchaseId, {
      hiddenSupplierIds: [...this.hiddenSupplierIds()]
    });
    this.refreshStickyStyles();
  }

  /** Recolhe todas as colunas de fornecedor sem item selecionado: sobra só o que está em comparação. */
  collapseSuppliersWithoutSelection(): void {
    const ids = (this.quoteMapData().supplierIds ?? []).filter(id => !this.hasSelectedProducts(id));
    this.hiddenSupplierIds.set(new Set(ids));
    this.preferences.patch(this.data()?.purchaseId, { hiddenSupplierIds: ids });
    this.refreshStickyStyles();
  }

  expandAllSuppliers(): void {
    this.hiddenSupplierIds.set(new Set());
    this.preferences.patch(this.data()?.purchaseId, { hiddenSupplierIds: [] });
    this.refreshStickyStyles();
  }

  setSupplierSort(sort: SupplierSort): void {
    this.supplierSort.set(sort);
    this.preferences.patch(this.data()?.purchaseId, { supplierSort: sort });
    this.refreshStickyStyles();
  }

  /**
   * O MatTable só recalcula os deslocamentos das colunas fixas quando as definições de coluna
   * mudam ou no resize do contêiner. Largura, ordem ou visibilidade alteradas por nós exigem
   * o recálculo explícito, depois que o DOM assentou.
   */
  private refreshStickyStyles(): void {
    setTimeout(() => this.table()?.updateStickyColumnStyles());
  }

  /** Reaplica colunas recolhidas e ordenação guardadas para esta solicitação (só ids que ainda existem). */
  private restorePreferences(purchaseId: string | undefined, data: QuotationMapBoardData): void {
    const prefs = this.preferences.get(purchaseId);
    const existing = new Set(data.supplierIds ?? []);
    const hidden = (prefs.hiddenSupplierIds ?? []).filter(id => existing.has(id));
    this.hiddenSupplierIds.set(new Set(hidden));
    if (prefs.supplierSort) this.supplierSort.set(prefs.supplierSort);
  }

  /** Soma preço × quantidade dos itens que o fornecedor cotou; null se não cotou nenhum. */
  private getSupplierQuotedTotal(supplierId: string): number | null {
    let total = 0;
    let quotedAny = false;
    for (const item of this.quoteMapData().items ?? []) {
      const supplier = item.suppliers.get(supplierId);
      if (supplier?.price) {
        total += supplier.price * item.quantity;
        quotedAny = true;
      }
    }
    return quotedAny ? total : null;
  }

  private getSupplierBestPriceCount(supplierId: string): number {
    return (this.quoteMapData().items ?? []).filter(item => !!item.suppliers.get(supplierId)?.bestPrice).length;
  }

  // ---- validade da cotação ----

  /** Dias até a validade (negativo = vencida), contando por dia civil. */
  getValidityDaysLeft(supplierId: string): number | null {
    const raw = this.getSupplierValidity(supplierId);
    if (!raw) return null;
    const target = new Date(raw);
    if (Number.isNaN(target.getTime())) return null;
    target.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((target.getTime() - today.getTime()) / 86_400_000);
  }

  getSupplierValidityStatus(supplierId: string): 'expired' | 'expiring' | 'valid' | null {
    const days = this.getValidityDaysLeft(supplierId);
    if (days === null) return null;
    if (days < 0) return 'expired';
    if (days <= 3) return 'expiring';
    return 'valid';
  }

  getValidityIcon(supplierId: string): string {
    switch (this.getSupplierValidityStatus(supplierId)) {
      case 'expired':
        return 'event_busy';
      case 'expiring':
        return 'schedule';
      default:
        return 'hourglass_bottom';
    }
  }

  getValidityLabel(supplierId: string): string {
    const raw = this.getSupplierValidity(supplierId);
    const days = this.getValidityDaysLeft(supplierId);
    if (days === null) return raw ? `Validade: ${raw}` : 'Validade: --';
    const dateLabel = formatDate(raw, 'dd/MM/yyyy', this.locale);
    if (days < 0) return `Vencida em ${dateLabel}`;
    if (days === 0) return 'Vence hoje';
    if (days === 1) return 'Vence amanhã';
    if (days <= 3) return `Vence em ${days} dias`;
    return `Validade: ${dateLabel}`;
  }

  /** Fornecedores com item selecionado cuja cotação já venceu — para avisar antes de aprovar. */
  getExpiredSelectedSupplierNames(): string[] {
    return (this.quoteMapData().supplierIds ?? [])
      .filter(id => this.hasSelectedProducts(id) && this.getSupplierValidityStatus(id) === 'expired')
      .map(id => this.getSupplierName(id));
  }

  getSupplierName(supplierId: string): string {
    for (const item of this.quoteMapData().items) {
      const supplier = item.suppliers.get(supplierId);
      if (supplier && supplier.companyName) {
        return supplier.companyName;
      }
    }
    return supplierId;
  }

  getSupplierOfferId(supplierId: string): string {
    for (const item of this.quoteMapData().items) {
      const supplier = item.suppliers.get(supplierId);
      if (supplier && supplier.offerId) {
        return supplier.offerId;
      }
    }
    return '';
  }

  getSupplierRespondedAt(supplierId: string): string {
    for (const item of this.quoteMapData().items) {
      const supplier = item.suppliers.get(supplierId);
      if (supplier && supplier.respondedAt) {
        return supplier.respondedAt;
      }
    }
    return '';
  }

  getSupplierStatus(supplierId: string): QuotationMapSupplierStatus {
    for (const item of this.quoteMapData().items) {
      const supplier = item.suppliers.get(supplierId);
      if (supplier) {
        return supplier.status;
      }
    }
    return QuotationMapSupplierStatus.PENDING;
  }

  getSupplierCnpj(supplierId: string): string {
    for (const item of this.quoteMapData().items ?? []) {
      const supplier = item.suppliers.get(supplierId);
      if (supplier?.cnpj) return supplier.cnpj;
    }
    return '';
  }

  getSupplierEmail(supplierId: string): string {
    for (const item of this.quoteMapData().items) {
      const supplier = item.suppliers.get(supplierId);
      if (supplier && supplier.email) {
        return supplier.email;
      }
    }
    return '';
  }

  copySupplierLink(supplierId: string): void {
    let link = '';
    for (const item of this.quoteMapData().items) {
      const supplier = item.suppliers.get(supplierId);
      if (supplier && supplier.link) {
        link = supplier.link;
        break;
      }
    }

    if (link) {
      this.clipboard.copy(link);
      this.snackBar.success('Link copiado com sucesso!');
    }
  }

  /** Evita abrir dois dialogs com clique duplo no "Adicionar". */
  private newSupplierDialogOpen = false;

  openNewSupplierDialog(): void {
    if (this.newSupplierDialogOpen) return;
    this.newSupplierDialogOpen = true;
    const data: NewSupplierDialogData = {
      // Itens para a escolha "Todos os itens / Escolher itens" do dialog.
      items: (this.quoteMapData().items ?? []).map(item => ({
        productId: item.productId ?? item.code,
        code: item.code,
        product: item.product,
        branch: item.branch,
        previousSupplierIds: item.purchaseHistory?.previousSuppliers?.map(s => s.id),
        currentSupplierCnpjs: Array.from(item.suppliers.entries())
          .filter(([, quote]) => !quote.notParticipating)
          .map(([id]) => this.getSupplierCnpj(id).replace(/[^0-9A-Z]/gi, '').toUpperCase())
          .filter(Boolean)
      }))
    };
    const dialogRef = this.dialog.open<NewSupplierDialogComponent, NewSupplierDialogData, PurchaseSupplier>(
      NewSupplierDialogComponent,
      // Foco inicial no CNPJ (cdkFocusInitial): é por ele que o preenchimento começa.
      { autoFocus: true, data }
    );

    // O dialog espera a criação: botão em "Adicionando…" e, se der erro, continua aberto com os dados.
    dialogRef.componentInstance.savesBeforeClosing = true;
    const saveSubscription = dialogRef.componentInstance.save.subscribe(event => this.onCreateSupplier.emit(event));

    const searchCnpjSubscription = dialogRef.componentInstance.searchCNPJ.subscribe(
      ({ cnpj, callback }: { cnpj: string; callback: (user: UserResponse | null) => void }) => {
        this.userService.findByCNPJ(cnpj).subscribe({
          next: user => callback(user ?? null),
          error: () => callback(null)
        });
      }
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.newSupplierDialogOpen = false;
        searchCnpjSubscription.unsubscribe();
        saveSubscription.unsubscribe();
      });
  }

  protected canDeleteSupplier(supplierId: string): boolean {
    return !this.getSupplierRespondedAt(supplierId);
  }

  protected confirmDeleteSupplier(supplierId: string): void {
    this._dialogService
      .open(
        {
          headerTitle: 'Remover Fornecedor',
          title: 'Deseja remover esse fornecedor dessa cotação?',
          description: 'O fornecedor será removido e não poderá enviar o orçamento para cotação.',
          labelConfirm: 'Desejo remover',
          labelCancel: 'Cancelar'
        },
        ModalTypes.DANGER
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(confirmed => {
        if (confirmed === Action.CONFIRM) {
          this.onDeleteSupplier.emit({ supplierId });
        }
      });
  }

  addSupplierToQuotationProduct(item: QuotationMapItem, supplierId: string): void {
    const supplier = item.suppliers.get(supplierId);
    const supplierName = supplier?.companyName || this.getSupplierName(supplierId);

    this._dialogService
      .open(
        {
          headerTitle: 'Adicionar cotação',
          title: `Deseja adicionar ${supplierName} à cotação deste produto?`,
          description:
            'O fornecedor será incluído na cotação deste produto e poderá enviar o orçamento.',
          labelConfirm: 'Adicionar',
          labelCancel: 'Cancelar'
        },
        ModalTypes.INFO
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(confirmed => {
        if (confirmed === Action.CONFIRM) {
          this.onAddSupplierToProduct.emit({
            purchaseId: this.quoteMapData().purchaseId,
            productId: item.productId ?? item.code,
            supplierId,
            supplierName,
            supplierCnpj: supplier?.cnpj,
            supplierPhone: supplier?.phone,
            supplierEmail: supplier?.email || this.getSupplierEmail(supplierId)
          });
        }
      });
  }

  getSupplierLink(supplierId: string): string {
    for (const item of this.quoteMapData().items) {
      const supplier = item.suppliers.get(supplierId);
      if (supplier?.link) {
        return supplier.link;
      }
    }

    return '';
  }

  getSupplierValidity(supplierId: string): string {
    for (const item of this.quoteMapData().items) {
      const supplier = item.suppliers.get(supplierId);
      if (supplier && supplier.validity) {
        return supplier.validity;
      }
    }
    return '';
  }

  getQuotationMapSupplier(
    item: QuotationMapItem,
    supplierId: string
  ): QuotationMapSupplier | undefined {
    return item.suppliers?.get(supplierId);
  }

  isSelectionHighlighted(
    item: QuotationMapItem | QuotationMapFooterRowData | { isValidityRow?: boolean; isFooterRow?: boolean; type?: string },
    supplierId: string
  ): boolean {
    const row = item as { isValidityRow?: boolean; isFooterRow?: boolean; type?: string };
    if (!row.isValidityRow && !row.isFooterRow) {
      const supplier = this.getQuotationMapSupplier(item as QuotationMapItem, supplierId);
      return !!(supplier?.selected || supplier?.negotiatedSelected);
    }
    if (row.isFooterRow && row.type === 'freight') {
      if (!this.hasSelectedProducts(supplierId)) return false;
      const freight = this.getSupplierFreight(supplierId);
      return !!(freight?.selected || freight?.negotiatedSelected);
    }
    return false;
  }

  protected hasPurchaseHistory(item: QuotationMapItem): boolean {
    const history = item.purchaseHistory;

    if (!history) {
      return false;
    }

    return (
      history.lastPurchasePrice != null ||
      history.bestPrice != null ||
      history.averageQuantity != null ||
      history.lastPurchaseDate != null
    );
  }

  protected getPurchaseHistory(item: QuotationMapItem) {
    if (!item.purchaseHistory) return item.purchaseHistory;
    const prices = Array.from(item.suppliers.values())
      .map(s => s.price)
      .filter((p): p is number => typeof p === 'number' && p > 0);
    return {
      ...item.purchaseHistory,
      currentLowestPrice: prices.length ? Math.min(...prices) : null,
      productId: item.productId,
      productName: item.product,
      purchaseId: this.quoteMapData().purchaseId
    };
  }

  isSupplierNotParticipating(item: QuotationMapItem, supplierId: string): boolean {
    return item.suppliers.get(supplierId)?.notParticipating === true;
  }

  isSupplierSuggestedPending(item: QuotationMapItem, supplierId: string): boolean {
    const supplier = item.suppliers.get(supplierId);
    return supplier?.suggestedPending === true && supplier.notParticipating !== true;
  }

  /**
   * Um fornecedor sugerido ainda sem nenhum produto aprovado (`isOnlyPendingSuggestion`
   * calculado no backend a partir de TODOS os vínculos produto-fornecedor da
   * oferta, não só os itens exibidos neste board) não deve ter ações de
   * convite (copiar link/reenviar e-mail) — ele ainda não tem acesso real à
   * cotação. Se já tiver ao menos 1 produto aprovado, é um participante
   * legítimo e as ações continuam disponíveis normalmente.
   */
  protected hasOnlyPendingSuggestedProducts(supplierId: string): boolean {
    for (const item of this.quoteMapData().items) {
      const supplier = item.suppliers.get(supplierId);
      if (supplier) return supplier.isOnlyPendingSuggestion === true;
    }
    return false;
  }

  protected isSuggestedByCotAI(supplierId: string): boolean {
    for (const item of this.quoteMapData().items) {
      const supplier = item.suppliers.get(supplierId);
      if (supplier) return supplier.source === 'eBarn CotAI';
    }
    return false;
  }

  protected getSupplierOfertaFornecedorId(supplierId: string): string | undefined {
    for (const item of this.quoteMapData().items) {
      const supplier = item.suppliers.get(supplierId);
      if (supplier?.ofertaFornecedorId) return supplier.ofertaFornecedorId;
    }
    return undefined;
  }

  /** Há ao menos um produto deste fornecedor ainda como sugestão pendente (inclusive com outros produtos aprovados). */
  protected hasPendingSuggestedProducts(supplierId: string): boolean {
    return this.quoteMapData().items.some(item => item.suppliers.get(supplierId)?.suggestedPending === true);
  }

  /** Atalho do cabeçalho: aceita todas as sugestões pendentes do fornecedor, uma por produto. */
  protected approveSuggestedSupplier(supplierId: string): void {
    const productIds: string[] = [];
    for (const item of this.quoteMapData().items) {
      const supplier = item.suppliers.get(supplierId);
      if (supplier?.suggestedPending && item.productId) {
        productIds.push(item.productId);
      }
    }
    this.emitSuggestedStatus(supplierId, productIds, PurchaseSupplierLinkStatus.APROVADO);
  }

  /** Ação da célula "Sugestão pendente": aceita só o fornecedor neste produto. */
  protected approveSuggestedProduct(item: QuotationMapItem, supplierId: string): void {
    this.emitSuggestedStatus(supplierId, item.productId ? [item.productId] : [], PurchaseSupplierLinkStatus.APROVADO);
  }

  /** Ação da célula "Sugestão pendente": recusa só o fornecedor neste produto. */
  protected rejectSuggestedProduct(item: QuotationMapItem, supplierId: string): void {
    this.emitSuggestedStatus(supplierId, item.productId ? [item.productId] : [], PurchaseSupplierLinkStatus.REJEITADO);
  }

  private emitSuggestedStatus(
    supplierId: string,
    productIds: string[],
    status: PurchaseSupplierLinkStatus.APROVADO | PurchaseSupplierLinkStatus.REJEITADO
  ): void {
    const ofertaFornecedorId = this.getSupplierOfertaFornecedorId(supplierId);
    if (!ofertaFornecedorId || productIds.length === 0) return;

    this.onUpdateSuggestedSupplierStatus.emit({ supplierId: ofertaFornecedorId, productIds, status });
  }

  getSupplierFooterData(supplierId: string, type: string): string | number {
    for (const item of this.quoteMapData().items) {
      const supplier = item.suppliers.get(supplierId);
      if (supplier && supplier.status === QuotationMapSupplierStatus.RESPONDED) {
        switch (type) {
          case 'observations':
            return supplier.observations || '';
          case 'paymentMethod':
            return supplier.paymentMethod || 'N/A';
          case 'paymentTerms':
            return supplier.paymentTerms || 'N/A';
          default:
            break;
        }
      }
    }
    switch (type) {
      case 'observations':
        return '';
      default:
        return '-';
    }
  }

  getSupplierFreight(supplierId: string): QuotationMapSupplierFreight | undefined {
    return this.quoteMapData().supplierFreights?.get(supplierId);
  }

  private createSupplierFreight(supplierId: string, freight = 0): QuotationMapSupplierFreight {
    return {
      supplierId,
      freight,
      selected: false,
      negotiatedFreight: null,
      negotiatedSelected: false
    };
  }

  private createSupplierEntry(supplierId: string): QuotationMapSupplier {
    return {
      id: supplierId,
      offerId: this.getSupplierOfferId(supplierId),
      companyName: this.getSupplierName(supplierId),
      price: 0,
      selected: false,
      bestPrice: false,
      negotiatedPrice: 0,
      negotiatedSelected: false,
      respondedAt: this.getSupplierRespondedAt(supplierId),
      validity: this.getSupplierValidity(supplierId),
      status: QuotationMapSupplierStatus.RESPONDED,
      observations: '',
      paymentMethod: '',
      paymentTerms: ''
    };
  }

  protected hasSelectedProducts(supplierId: string, data = this.quoteMapData()): boolean {
    return data.items.some(item => {
      const supplier = item.suppliers.get(supplierId);
      return !!supplier && (supplier.selected || supplier.negotiatedSelected);
    });
  }

  protected shouldShowFreightRestriction(
    supplierId: string,
    type: 'quoted' | 'negotiated'
  ): boolean {
    if (!this.hasSelectedProducts(supplierId)) {
      return true;
    }

    const freight = this.getSupplierFreight(supplierId);
    if (!freight) {
      return false;
    }

    if (type === 'quoted') {
      return freight.selected && !freight.negotiatedSelected;
    }

    return Boolean(freight.negotiatedSelected) && !freight.selected;
  }

  protected getFreightCheckboxTooltip(
    supplierId: string,
    type: 'quoted' | 'negotiated'
  ): string {
    if (!this.shouldShowFreightRestriction(supplierId, type)) {
      return '';
    }

    if (!this.hasSelectedProducts(supplierId)) {
      return this.freightSupplierNotSelectedTooltip;
    }

    return this.freightRequiredTooltip;
  }

  private getSelectedFreightValue(supplierId: string): number {
    if (!this.hasSelectedProducts(supplierId)) return 0;

    const freight = this.getSupplierFreight(supplierId);
    if (!freight) return 0;

    if (freight.negotiatedSelected) {
      return freight.negotiatedFreight ?? freight.freight ?? 0;
    }

    if (freight.selected) {
      return freight.freight ?? 0;
    }

    return 0;
  }

  private syncFreightSelectionForSuppliers(
    data: QuotationMapBoardData,
    supplierIds = data.supplierIds
  ): QuotationMapBoardData {
    const supplierFreights = new Map(data.supplierFreights ?? []);

    supplierIds.forEach(supplierId => {
      const current = supplierFreights.get(supplierId) ?? this.createSupplierFreight(supplierId);
      const hasProducts = this.hasSelectedProducts(supplierId, data);
      let next: QuotationMapSupplierFreight;

      if (!hasProducts) {
        next = { ...current, selected: false, negotiatedSelected: false };
      } else if (!current.selected && !current.negotiatedSelected) {
        next = { ...current, selected: true, negotiatedSelected: false };
      } else {
        next = current;
      }

      this.updateFreightSelectionsMap(supplierId, {
        selected: next.selected,
        negotiatedSelected: !!next.negotiatedSelected,
        negotiatedPrice: next.negotiatedFreight ?? null
      });
      supplierFreights.set(supplierId, next);
    });

    return { ...data, supplierFreights };
  }

  toggleFreightSelection(supplierId: string, event: MatCheckboxChange): void {
    if (!this.hasSelectedProducts(supplierId)) {
      event.source.checked = false;
      return;
    }

    const checked = event.checked;
    const current = this.getSupplierFreight(supplierId) ?? this.createSupplierFreight(supplierId);

    if (!checked && !current.negotiatedSelected) {
      event.source.checked = true;
      return;
    }
    const next: QuotationMapSupplierFreight = {
      ...current,
      selected: checked,
      negotiatedSelected: checked ? false : current.negotiatedSelected
    };

    this.updateFreightSelectionsMap(supplierId, {
      selected: next.selected,
      negotiatedSelected: !!next.negotiatedSelected
    });
    this.patchSupplierFreight(supplierId, next);
  }

  toggleNegotiatedFreight(
    supplierId: string,
    event: MatCheckboxChange,
    input?: HTMLInputElement
  ): void {
    if (!this.hasSelectedProducts(supplierId)) {
      event.source.checked = false;
      return;
    }

    const checked = event.checked;
    const current = this.getSupplierFreight(supplierId) ?? this.createSupplierFreight(supplierId);

    if (checked && !(parseMoney(this.getFreightFormControl(supplierId)?.value) > 0)) {
      event.source.checked = false;
      input?.focus();
      this.snackBar.info('Digite o frete negociado: ao digitar, ele passa a valer.');
      return;
    }

    if (!checked && !current.selected) {
      event.source.checked = true;
      return;
    }

    const next: QuotationMapSupplierFreight = {
      ...current,
      selected: checked ? false : current.selected,
      negotiatedSelected: checked
    };

    this.updateFreightSelectionsMap(supplierId, {
      selected: next.selected,
      negotiatedSelected: next.negotiatedSelected
    });
    this.patchSupplierFreight(supplierId, next);
    this.updateNegotiatedFreight(supplierId, this.getFreightFormControl(supplierId)?.value ?? null);

    if (checked && input) {
      setTimeout(() => input.focus());
    }
  }

  private patchSupplierFreight(supplierId: string, freight: QuotationMapSupplierFreight): void {
    const supplierFreights = new Map(this.quoteMapData().supplierFreights ?? []);
    supplierFreights.set(supplierId, freight);
    this.quoteMapData.set({ ...this.quoteMapData(), supplierFreights });
  }

  calculateSupplierTotal(supplierId: string): number {
    const productsTotal = this.quoteMapData().items.reduce((total, item) => {
      const quotationMapSupplier = item.suppliers.get(supplierId);
      if (quotationMapSupplier) {
        if (quotationMapSupplier.negotiatedSelected && quotationMapSupplier.negotiatedPrice != null) {
          total += quotationMapSupplier.negotiatedPrice * item.quantity;
        } else if (quotationMapSupplier.selected) {
          total += quotationMapSupplier.price * item.quantity;
        }
      }
      return total;
    }, 0);

    return productsTotal + this.getSelectedFreightValue(supplierId);
  }

  trackByRow(
    index: number,
    item:
      | QuotationMapItem
      | QuotationMapFooterRowData
      | { isValidityRow: boolean; isFooterRow: boolean }
  ): string {
    if ('isFooterRow' in item && item.isFooterRow) {
      return `footer_${(item as QuotationMapFooterRowData).type}`;
    }
    if ('isValidityRow' in item && item.isValidityRow) {
      return 'validity_row';
    }
    return (item as QuotationMapItem).code;
  }

  private updateNegotiatedFreight(supplierId: string, value: number | null): void {
    const current = this.getSupplierFreight(supplierId) ?? this.createSupplierFreight(supplierId);
    this.patchSupplierFreight(supplierId, { ...current, negotiatedFreight: value });
    this.updateFreightSelectionsMap(supplierId, { negotiatedPrice: value });
  }

  private updateNegotiatedPrice(code: string, supplierId: string, value: number | null): void {
    const updatedData = { ...this.quoteMapData() };
    const itemIndex = updatedData.items.findIndex(item => item.code === code);

    if (itemIndex !== -1) {
      const item = updatedData.items[itemIndex];
      const supplier = item.suppliers.get(supplierId) || this.createSupplierEntry(supplierId);
      const updatedSuppliers = new Map(item.suppliers);

      updatedSuppliers.set(supplierId, { ...supplier, negotiatedPrice: value || 0 });
      updatedData.items[itemIndex] = { ...item, suppliers: updatedSuppliers };
      this.quoteMapData.set(updatedData);

      this.updateSelectionsMap(code, supplierId, { negotiatedPrice: value });
    }
  }

  openAproveDialog(): void {
    const hasAnySelection = this.quoteMapData().items.some(item =>
      Array.from(item.suppliers.values()).some(hasPricedSelection)
    );
    if (!hasAnySelection) {
      this.snackBar.warning('Selecione ao menos um preço para aprovar a cotação');
      return;
    }

    // 1) quadro de revisão (somente leitura); 2) justificativa; 3) aprovação
    const review = this.buildApprovalReview();
    this.dialog
      .open<ApprovalReviewDialogComponent, ApprovalReviewData, ApprovalReviewResult>(ApprovalReviewDialogComponent, {
        size: DialogSize.LG,
        data: review
      })
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(result => {
        if (result === 'continue') this.openJustificationDialog(review.hasExpired);
      });
  }

  private openJustificationDialog(hasExpired: boolean): void {
    this._dialogService
      .openWithJustification(
        {
          headerTitle: 'Aprovação da cotação',
          title: 'Deseja informar a justificativa ao aprovar essa cotação?',
          description: '',
          labelConfirm: 'Confirmar e aprovar',
          labelCancel: 'Aprovar sem justificativa'
        },
        {
          justificationPlaceholder: 'Justificativa da aprovação (fica registrada nos comentários da solicitação).',
          justificationRequired: true
        },
        hasExpired ? ModalTypes.WARNING : ModalTypes.SUCCESS
      )
      .subscribe(result => {
        if (result.action === Action.CONFIRM) {
          this.onApproveProducts(result.justification);
          return;
        }

        if (result.action === Action.CANCEL) {
          this.onApproveProducts(DEFAULT_APPROVAL_JUSTIFICATION);
        }
      });
  }

  /** Monta o quadro de revisão a partir do estado atual, com os mesmos cálculos do rodapé e do cabeçalho. */
  private buildApprovalReview(): ApprovalReviewData {
    const data = this.quoteMapData();
    return buildApprovalReview(data, {
      accentColor: index => this.getSupplierHeaderColors(index).bar,
      locale: this.locale
    });
  }

  onApproveProducts(justification?: string): void {
    const selectedItems = this.quoteMapData().items.filter(item =>
      Array.from(item.suppliers.values()).some(hasPricedSelection)
    );
    const negotiatedFreights = this.quoteMapData()
      .supplierIds.map(supplierId => this.getSupplierFreight(supplierId))
      .filter((freight): freight is QuotationMapSupplierFreight => {
        return !!freight?.negotiatedSelected && this.hasSelectedProducts(freight.supplierId);
      })
      .map(freight => ({
        supplierId: freight.supplierId,
        freight: freight.negotiatedFreight ?? freight.freight
      }));

    this.onApprove.emit({ items: selectedItems, negotiatedFreights, justification });
  }
}
